import type { PlaceType } from 'BomdisoftComponents/types';

export type AutocompleteService = { getPlacePredictions: (request: { input: string }, callback: (results?: readonly PlaceType[]) => void) => void };

export type GeocoderResult = {
  address_components: { long_name: string; types: string[] }[];
  formatted_address: string;
  geometry: { location: { lat: () => number; lng: () => number } };
};

export type Geocoder = { geocode: (request: { placeId: string }, callback: (results: GeocoderResult[] | null, status: string) => void) => void };

const AUTOCOMPLETE_SERVICE_PATH = ['google', 'maps', 'places', 'AutocompleteService'] as const;
const GEOCODER_PATH = ['google', 'maps', 'Geocoder'] as const;

export const createAutocompleteService = (view: Window): AutocompleteService | undefined => {
  const service = construct(view, AUTOCOMPLETE_SERVICE_PATH);
  return service !== undefined && isAutocompleteService(service) ? service : undefined;
};

export const createGeocoder = (view: Window): Geocoder | undefined => {
  const geocoder = construct(view, GEOCODER_PATH);
  return geocoder !== undefined && isGeocoder(geocoder) ? geocoder : undefined;
};

export const hasGeocoder = (view: Window): boolean => typeof readPath(view, GEOCODER_PATH) === 'function';

const construct = (view: Window, path: readonly string[]): object | undefined => {
  const Service = readPath(view, path);
  if (typeof Service !== 'function') return undefined;
  const instance: unknown = Reflect.construct(Service, []);
  return isObject(instance) ? instance : undefined;
};

const readPath = (view: Window, path: readonly string[]): unknown => path.reduce<unknown>((value, key) => (isObject(value) ? Reflect.get(value, key) : undefined), view);

const isObject = (value: unknown): value is object => (typeof value === 'object' && value !== null) || typeof value === 'function';

const isAutocompleteService = (value: object): value is AutocompleteService => typeof Reflect.get(value, 'getPlacePredictions') === 'function';

const isGeocoder = (value: object): value is Geocoder => typeof Reflect.get(value, 'geocode') === 'function';
